import {
  aliasToPublicSignKey,
  createConversation,
  type Credentials,
  getConversations,
  sendMessage,
} from "@alice-and-bot/core";
import type { User } from "@instantdb/react";
import { computed, signal } from "@preact/signals";
import type { JSX } from "preact";
import { LocationProvider, Route, Router, useLocation } from "preact-iso";
import hydrate from "preact-iso/hydrate";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast, Toaster } from "react-hot-toast";
import { SocialIcon } from "react-social-icons";
import {
  authUrl,
  claimTwitterHandle,
  createPortalSession,
  deleteAccount,
  disconnectTwitterAccount,
  generateApiToken,
  linkTwitterHandle,
  messageStats,
  nextCreditReturn,
  type PaymentHistoryEvent,
  quotaLeft,
  revokeToken,
  sendMagicCode,
  signInWithMagicCode,
  signOut,
  syncCheckoutSession,
  totalQuota,
  updateUserPref,
  type UsageEvent,
  useAliceAndBotCredentials,
  useAuth,
  usePaymentHistory,
  useUsageHistory,
  useUserCurrentTierPrice,
  useUserIsPro,
  useUserSettings,
} from "./db.ts";
import { captureClientErrors } from "@uri/anomalisa";
import { installEventRejectionSuppression } from "./suppressEventRejections.ts";
import { anomalisaToken } from "../../shared/analytics.ts";
import {
  freeTierMessageLimit,
  freeTierQuota,
  monthInMs,
  pricingTiers,
} from "../../shared/quota.ts";
import "./style.css";
import { useIsMobile } from "./uiComponents.tsx";
import { Landing } from "./landing.tsx";

if (typeof window !== "undefined") {
  installEventRejectionSuppression(window);
  captureClientErrors({ token: anomalisaToken });
  import("posthog-js")
    .then(({ posthog }) =>
      posthog.init("phc_hiploMbwm48Ls04QF0SPc8VN0qtNMY8G1FwSjrpuErr", {
        api_host: "https://us.posthog.com",
      })
    )
    .catch((err) => {
      console.error("Failed to initialize PostHog:", err);
    });
}

export const ROUTES = {
  HOME: "/",
  APP: "/app",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  SUBS: "/subs",
  DOCS: "/docs",
  SETTINGS: "/settings",
  PRICING: "/pricing",
} as const;

const botPublicSignKey = signal<string | null>(null);
const chatElementReadySignal = signal(false);
let chatElementError: string | null = null;

if (typeof window !== "undefined") {
  aliasToPublicSignKey("find_scene")
    .then((result: { publicSignKey: string } | { error: string }) => {
      if ("publicSignKey" in result) {
        botPublicSignKey.value = result.publicSignKey;
      } else console.error("Failed to resolve bot alias:", result);
    })
    .catch((err) => {
      console.error("Failed to resolve bot alias via API:", err);
    });
  import("@alice-and-bot/core/components")
    .then(() => {
      chatElementReadySignal.value = true;
    })
    .catch((error: unknown) => {
      chatElementError = error instanceof Error ? error.message : String(error);
    });
}

const Spinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexGrow: 1,
    }}
  >
    <div
      style={{
        width: 24,
        height: 24,
        border: "2px solid #ffffff20",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "spin 0.6s linear infinite",
      }}
    />
  </div>
);

const DemoVideo = () => (
  <iframe
    style={{
      width: "100%",
      maxWidth: 560,
      aspectRatio: "16/9",
      borderRadius: 12,
      border: "none",
    }}
    src="https://www.youtube.com/embed/AqCkLoZATiM?autoplay=1"
    title="YouTube video player"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowFullScreen
  />
);

const Subs = () => {
  const [subsText, setSubsText] = useState("");
  const subsUrl = typeof window !== "undefined"
    ? new URL(globalThis.window.location.href).searchParams.get("url")
    : null;
  useEffect(() => {
    if (subsUrl) {
      fetch(subsUrl)
        .then((x) => x.text())
        .then(setSubsText)
        .catch((err) => {
          console.error("Failed to fetch subtitles:", err);
          setSubsText("Failed to load subtitles.");
        });
    }
  }, [subsUrl]);
  return <div className="display-linebreak">{subsText}</div>;
};

const primaryButtonStyle: JSX.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "0 24px",
  height: 48,
  minHeight: 48,
  textDecoration: "none",
  fontWeight: 600,
  borderRadius: 8,
  background: "#311d2b",
  color: "#e0c0d8",
  fontSize: 16,
  letterSpacing: 0.3,
  border: "none",
  cursor: "pointer",
  transition: "opacity 0.2s",
  boxSizing: "border-box",
};

const primaryButtonBlockStyle: JSX.CSSProperties = {
  ...primaryButtonStyle,
  display: "flex",
  width: "100%",
};

const secondaryButtonStyle: JSX.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "0 20px",
  height: 48,
  minHeight: 48,
  textDecoration: "none",
  fontWeight: 600,
  borderRadius: 8,
  background: "transparent",
  color: "#e0c0d8",
  fontSize: 14,
  letterSpacing: 0.3,
  border: "1px solid #311d2b",
  cursor: "pointer",
  transition: "opacity 0.2s",
  boxSizing: "border-box",
};

const navLinkStyle: JSX.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 14px",
  borderRadius: 999,
  background: "transparent",
  color: "#e0c0d8",
  fontSize: 14,
  fontWeight: 500,
  textDecoration: "none",
  border: "1px solid transparent",
  transition: "background 0.2s, border-color 0.2s",
};

const loginInputStyle: JSX.CSSProperties = {
  width: "100%",
  height: 48,
  minHeight: 48,
  padding: "0 14px",
  borderRadius: 8,
  border: "1px solid #ffffff20",
  background: "#ffffff08",
  color: "#ccc",
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
};

const legalPageStyle = {
  maxWidth: 720,
  margin: "0 auto",
  padding: "40px 24px",
  lineHeight: 1.7,
  width: "100%",
  boxSizing: "border-box" as const,
  minWidth: 0,
};

const legalH1Style = { fontSize: 28, marginBottom: 8 };
const legalDateStyle = { color: "#999", marginBottom: 32 };
const legalH2Style = { fontSize: 20, marginTop: 32, marginBottom: 12 };
const legalFooterStyle = { marginTop: 40, color: "#999", fontSize: 14 };

const Terms = () => (
  <div style={legalPageStyle}>
    <h1 style={legalH1Style}>Terms of Service</h1>
    <p style={legalDateStyle}>Last updated: March 2026</p>

    <h2 style={legalH2Style}>1. Acceptance of Terms</h2>
    <p>
      By accessing or using find-scene, you agree to be bound by these Terms of
      Service. If you do not agree, do not use the service.
    </p>

    <h2 style={legalH2Style}>2. Description of Service</h2>
    <p>
      find-scene is an AI-powered tool that searches publicly available sources
      on the web for movie and TV show scenes, and processes them according to
      your specifications. find-scene does not own, host, or distribute the
      underlying media files. You acknowledge that find-scene acts solely as a
      search and processing tool and bears no responsibility for the content of
      third-party media.
    </p>

    <h2 style={legalH2Style}>3. Fair Use</h2>
    <p>
      Use of output clips is permitted only under the doctrine of{" "}
      <a href="https://fairuse.stanford.edu/overview/fair-use/what-is-fair-use/">
        fair use
      </a>
      , including commentary, criticism, parody, scholarly, or educational
      purposes. You are solely responsible for ensuring your use of extracted
      content complies with applicable copyright law.
    </p>

    <h2 style={legalH2Style}>4. User-Generated Content</h2>
    <p>
      Video clips and other content generated through the service may be used by
      find-scene for promotional or demonstrative purposes. Such content will
      never be publicly associated with your identity.
    </p>

    <h2 style={legalH2Style}>5. Payments and Refunds</h2>
    <p>
      Pro credits are purchased as one-time payments. Credits expire 30 days
      after purchase. Refunds are not available for unused credits, but we may
      grant courtesy credits at our discretion for service issues.
    </p>

    <h2 style={legalH2Style}>6. Prohibited Use</h2>
    <p>
      You may not use find-scene to distribute copyrighted material outside the
      scope of fair use, to harass or harm others, to interfere with the
      operation of the service, or to violate any applicable laws.
    </p>

    <h2 style={legalH2Style}>7. Limitation of Liability</h2>
    <p>
      The service is provided "as is" without warranties of any kind, express or
      implied. find-scene shall not be liable for any damages arising from the
      use or inability to use the service, including but not limited to loss of
      data, interruption of service, or claims by third parties.
    </p>

    <h2 style={legalH2Style}>8. Termination</h2>
    <p>
      We may suspend or terminate your access to the service at any time, with
      or without cause, and with or without notice.
    </p>

    <h2 style={legalH2Style}>9. Modifications</h2>
    <p>
      We reserve the right to modify these terms at any time. Continued use of
      the service after changes are posted constitutes acceptance of the revised
      terms.
    </p>

    <p style={legalFooterStyle}>
      For questions, contact us via{" "}
      <a href="https://discord.gg/JBbBgLy4kN">Discord</a>. See also our{" "}
      <a href={ROUTES.PRIVACY}>Privacy Policy</a>.
    </p>
  </div>
);

const Privacy = () => (
  <div style={legalPageStyle}>
    <h1 style={legalH1Style}>Privacy Policy</h1>
    <p style={legalDateStyle}>Last updated: March 2026</p>

    <h2 style={legalH2Style}>1. Information We Collect</h2>
    <p>
      We collect and store the following information for the purposes of service
      operation, development, and analytics:
    </p>
    <ul style={{ paddingLeft: 24 }}>
      <li>Account identifiers (email address or social login ID)</li>
      <li>Search queries and usage patterns</li>
      <li>Payment records (amount, date, payment method)</li>
    </ul>

    <h2 style={legalH2Style}>2. How We Use Your Information</h2>
    <p>
      Your information is used to operate and improve the service, process
      payments, provide customer support, and send service-related
      communications. We may send you occasional product update emails, which
      you can unsubscribe from at any time.
    </p>

    <h2 style={legalH2Style}>3. Information Sharing</h2>
    <p>
      We will never sell, rent, or share personally identifiable data associated
      with your account with third parties, except as required by law. We use
      third-party services (analytics, payment processing, email delivery) that
      may process your data under their own privacy policies.
    </p>

    <h2 style={legalH2Style}>4. Data Retention</h2>
    <p>
      We retain your account data and usage history for as long as your account
      is active. You may request deletion of your data by contacting us via
      Discord.
    </p>

    <h2 style={legalH2Style}>5. Cookies</h2>
    <p>
      We use essential cookies and local storage for authentication and session
      management. We do not use third-party tracking cookies.
    </p>

    <h2 style={legalH2Style}>6. Security</h2>
    <p>
      We take reasonable measures to protect your data, but no method of
      transmission or storage is 100% secure. We cannot guarantee absolute
      security.
    </p>

    <h2 style={legalH2Style}>7. Changes to This Policy</h2>
    <p>
      We may update this privacy policy from time to time. Continued use of the
      service after changes are posted constitutes acceptance of the revised
      policy.
    </p>

    <p style={legalFooterStyle}>
      For questions, contact us via{" "}
      <a href="https://discord.gg/JBbBgLy4kN">Discord</a>. See also our{" "}
      <a href={ROUTES.TERMS}>Terms of Service</a>.
    </p>
  </div>
);

const formatDuration = (ms: number): string => {
  const hours = Math.ceil(ms / 3600000);
  if (hours < 1) {
    const mins = Math.max(1, Math.ceil(ms / 60000));
    return `${mins} min${mins === 1 ? "" : "s"}`;
  }
  return hours >= 24
    ? `${Math.ceil(hours / 24)} day${Math.ceil(hours / 24) === 1 ? "" : "s"}`
    : `${hours} hour${hours === 1 ? "" : "s"}`;
};

const formatTargetDate = (ms: number): string =>
  new Date(Date.now() + ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const formatDateTime = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const formatRelativeTime = (timestamp: number): string => {
  const diff = Date.now() - timestamp;
  if (diff < 60000) return "Just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatPaymentType = (type: string): string =>
  type === "stripe"
    ? "Stripe Payment"
    : type === "patreon"
    ? "Patreon Pledge"
    : type === "credit"
    ? "Courtesy / Bonus Credit"
    : type === "crypto"
    ? "Crypto Payment"
    : type;

const historyModalOpen = signal(false);
const historyModalTab = signal<"downloads" | "billing">("downloads");

const openHistoryModal = (tab: "downloads" | "billing" = "downloads") => {
  historyModalTab.value = tab;
  historyModalOpen.value = true;
};

const HistoryModal = () => {
  const email = userEmail.value;
  const isPro = useUserIsPro(email);
  const authUser = instantActiveUser.value;
  const refreshToken = authUser?.refresh_token;
  const [portalLoading, setPortalLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { downloads, isLoading: downloadsLoading } =
    typeof window !== "undefined" &&
      (window as any).__MOCK_useUsageHistory__
      ? (window as any).__MOCK_useUsageHistory__(email)
      : useUsageHistory(email);
  const { payments, isLoading: paymentsLoading } =
    typeof window !== "undefined" &&
      (window as any).__MOCK_usePaymentHistory__
      ? (window as any).__MOCK_usePaymentHistory__(email)
      : usePaymentHistory(email);

  const creditsRemaining = email ? quotaLeft(email) : 0;
  const total = email ? totalQuota(email) : 0;
  const nextReturn = email ? nextCreditReturn(email) : null;
  const activeDownloads = downloads.filter(
    (d: UsageEvent) => d.time > Date.now() - monthInMs,
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") historyModalOpen.value = false;
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleManage = async () => {
    if (!email || !refreshToken) return;
    setPortalLoading(true);
    try {
      const url = await createPortalSession(email, refreshToken);
      globalThis.window.location.href = url;
    } catch (err) {
      console.error(err);
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const closeModal = () => {
    historyModalOpen.value = false;
  };

  const activeTab = historyModalTab.value;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        boxSizing: "border-box",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div
        ref={modalRef}
        style={{
          background: "#141018",
          border: "1px solid #ffffff20",
          borderRadius: 16,
          width: "100%",
          maxWidth: 640,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.85)",
          color: "#fff",
          overflow: "hidden",
          textAlign: "left",
        }}
      >
        <div
          style={{
            padding: "18px 20px 14px",
            borderBottom: "1px solid #ffffff15",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#e0c0d8",
              }}
            >
              Activity & Billing History
            </h2>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                color: "#888",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>{email}</span>
              <span
                style={{
                  fontSize: 10,
                  padding: "1px 5px",
                  borderRadius: 4,
                  background: isPro ? "#e8c872" : "#333",
                  color: isPro ? "#101010" : "#999",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {isPro ? "Pro" : "Free"}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close modal"
            style={{
              background: "transparent",
              border: "none",
              color: "#888",
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 10,
            padding: "14px 20px",
            background: "#ffffff04",
            borderBottom: "1px solid #ffffff10",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              background: "#ffffff08",
              borderRadius: 8,
              border: "1px solid #ffffff10",
            }}
          >
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              Credits Available
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: creditsRemaining <= 2 ? "#f87171" : "#4ade80",
              }}
            >
              {creditsRemaining} / {total}
            </div>
          </div>
          <div
            style={{
              padding: "8px 12px",
              background: "#ffffff08",
              borderRadius: 8,
              border: "1px solid #ffffff10",
            }}
          >
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              Active in 30d
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e0c0d8" }}>
              {activeDownloads.length} used
            </div>
          </div>
          <div
            style={{
              padding: "8px 12px",
              background: "#ffffff08",
              borderRadius: 8,
              border: "1px solid #ffffff10",
            }}
          >
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              Next Replenish
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: nextReturn ? "#fcd34d" : "#86efac",
              }}
            >
              {nextReturn
                ? `${formatDuration(nextReturn)} (${
                  formatTargetDate(nextReturn)
                })`
                : "All available"}
            </div>
          </div>
          <div
            style={{
              padding: "8px 12px",
              background: "#ffffff08",
              borderRadius: 8,
              border: "1px solid #ffffff10",
            }}
          >
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              Current Plan
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: isPro ? "#e8c872" : "#ccc",
              }}
            >
              {isPro ? "Pro Member" : "Free (20/mo)"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            padding: "12px 20px 0",
            gap: 8,
            borderBottom: "1px solid #ffffff12",
          }}
        >
          <button
            type="button"
            onClick={() => (historyModalTab.value = "downloads")}
            style={{
              padding: "8px 16px",
              background: activeTab === "downloads" ? "#311d2b" : "transparent",
              color: activeTab === "downloads" ? "#e0c0d8" : "#888",
              border: "none",
              borderBottom: activeTab === "downloads"
                ? "2px solid #e0c0d8"
                : "2px solid transparent",
              borderRadius: "6px 6px 0 0",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>🎬 Downloads & Credits</span>
            <span
              style={{
                fontSize: 11,
                padding: "1px 6px",
                borderRadius: 10,
                background: activeTab === "downloads"
                  ? "#ffffff20"
                  : "#ffffff10",
                color: "#ccc",
              }}
            >
              {downloads.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => (historyModalTab.value = "billing")}
            style={{
              padding: "8px 16px",
              background: activeTab === "billing" ? "#311d2b" : "transparent",
              color: activeTab === "billing" ? "#e0c0d8" : "#888",
              border: "none",
              borderBottom: activeTab === "billing"
                ? "2px solid #e0c0d8"
                : "2px solid transparent",
              borderRadius: "6px 6px 0 0",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>💳 Billing & Plans</span>
            <span
              style={{
                fontSize: 11,
                padding: "1px 6px",
                borderRadius: 10,
                background: activeTab === "billing" ? "#ffffff20" : "#ffffff10",
                color: "#ccc",
              }}
            >
              {payments.length}
            </span>
          </button>
        </div>

        <div
          style={{
            padding: 20,
            overflowY: "auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {activeTab === "downloads" && (
            <>
              <div
                style={{
                  fontSize: 12,
                  color: "#aaa",
                  background: "#ffffff06",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #ffffff10",
                  lineHeight: 1.4,
                }}
              >
                ℹ️ Each search/download consumes 1 credit. Credits return
                automatically 30 days after use.
              </div>

              {downloadsLoading
                ? (
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Spinner />
                  </div>
                )
                : downloads.length === 0
                ? (
                  <div
                    style={{
                      padding: "36px 16px",
                      textAlign: "center",
                      color: "#888",
                    }}
                  >
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🎬</div>
                    <p
                      style={{
                        margin: "0 0 6px",
                        fontSize: 15,
                        fontWeight: 500,
                        color: "#ccc",
                      }}
                    >
                      No downloads yet
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: "#777" }}>
                      When you search and download movie or TV scenes in chat,
                      your downloads will appear here.
                    </p>
                  </div>
                )
                : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {downloads.map((item: UsageEvent) => {
                      const isActive = item.time > Date.now() - monthInMs;
                      const msUntilReturn = Math.max(
                        0,
                        item.time + monthInMs - Date.now(),
                      );
                      return (
                        <div
                          key={item.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 14px",
                            background: "#ffffff06",
                            border: `1px solid ${
                              isActive ? "#ffffff18" : "#ffffff0a"
                            }`,
                            borderRadius: 8,
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span style={{ fontSize: 14 }}>🎬</span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#fff",
                                }}
                              >
                                Video Download / Search
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#888",
                                }}
                              >
                                ({formatRelativeTime(item.time)})
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                color: "#777",
                                marginLeft: 24,
                              }}
                            >
                              {formatDateTime(item.time)}
                            </span>
                          </div>

                          <div
                            style={{
                              textAlign: "right",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 4,
                                background: isActive
                                  ? "rgba(252, 211, 77, 0.15)"
                                  : "rgba(74, 222, 128, 0.12)",
                                color: isActive ? "#fcd34d" : "#4ade80",
                                border: `1px solid ${
                                  isActive
                                    ? "rgba(252, 211, 77, 0.3)"
                                    : "rgba(74, 222, 128, 0.25)"
                                }`,
                              }}
                            >
                              {isActive ? "-1 Credit (Active)" : "Replenished"}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                color: isActive ? "#fcd34d" : "#666",
                              }}
                            >
                              {isActive
                                ? `Replenishes ${
                                  formatTargetDate(msUntilReturn)
                                } (in ${formatDuration(msUntilReturn)})`
                                : `Replenished on ${
                                  formatDateTime(item.time + monthInMs)
                                }`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </>
          )}

          {activeTab === "billing" && (
            <>
              <div
                style={{
                  padding: "14px 16px",
                  background: isPro ? "#e8c8720a" : "#ffffff06",
                  border: isPro ? "1px solid #e8c87230" : "1px solid #ffffff12",
                  borderRadius: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: isPro ? "#e8c872" : "#e0c0d8",
                    }}
                  >
                    {isPro ? "✨ Active Pro Subscription" : "Free Plan"}
                  </div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {isPro
                      ? `${total} search credits per 30-day rolling window`
                      : "20 search credits per 30-day rolling window"}
                  </div>
                </div>

                {isPro
                  ? (
                    <button
                      type="button"
                      onClick={handleManage}
                      disabled={portalLoading}
                      style={{
                        ...secondaryButtonStyle,
                        padding: "6px 14px",
                        fontSize: 12,
                        cursor: portalLoading ? "wait" : "pointer",
                      }}
                    >
                      {portalLoading ? "Opening..." : "Manage in Stripe ↗"}
                    </button>
                  )
                  : (
                    <a
                      href={ROUTES.PRICING}
                      onClick={closeModal}
                      style={{
                        ...primaryButtonStyle,
                        padding: "6px 14px",
                        fontSize: 12,
                      }}
                    >
                      Upgrade to Pro ↗
                    </a>
                  )}
              </div>

              <div style={{ marginTop: 8 }}>
                <h3
                  style={{
                    margin: "0 0 10px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#999",
                  }}
                >
                  Payment & Credit Transactions
                </h3>

                {paymentsLoading
                  ? (
                    <div style={{ padding: 24, textAlign: "center" }}>
                      <Spinner />
                    </div>
                  )
                  : payments.length === 0
                  ? (
                    <div
                      style={{
                        padding: "28px 16px",
                        textAlign: "center",
                        color: "#888",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: 14,
                          color: "#bbb",
                        }}
                      >
                        No payment transactions on record
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                        Free plan users receive 20 rolling search credits at no
                        cost.
                      </p>
                    </div>
                  )
                  : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {payments.map((p: PaymentHistoryEvent) => (
                        <div
                          key={p.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 14px",
                            background: "#ffffff06",
                            border: "1px solid #ffffff10",
                            borderRadius: 8,
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#fff",
                              }}
                            >
                              {formatPaymentType(p.type)}
                            </span>
                            <span style={{ fontSize: 11, color: "#777" }}>
                              {formatDateTime(p.timestamp)}
                            </span>
                          </div>

                          <div
                            style={{
                              textAlign: "right",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#4ade80",
                              }}
                            >
                              ${p.amountUSD.toFixed(2)}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "1px 6px",
                                borderRadius: 4,
                                background: "rgba(74, 222, 128, 0.12)",
                                color: "#4ade80",
                                fontWeight: 600,
                              }}
                            >
                              Completed
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #ffffff15",
            background: "#ffffff03",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <a
            href={ROUTES.PRICING}
            onClick={closeModal}
            style={{
              fontSize: 12,
              color: "#e0c0d8",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            View Pricing & Plans →
          </a>
          <button
            type="button"
            onClick={closeModal}
            style={{
              padding: "6px 16px",
              background: "#311d2b",
              color: "#e0c0d8",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const CreditsLeft = () => {
  if (!userEmail.value) return null;
  const email = userEmail.value;
  const creditsRemaining = quotaLeft(email);
  const total = totalQuota(email);
  const nextReturn = nextCreditReturn(email);
  const msgStats = messageStats(email);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const creditsLabel = creditsRemaining !== null && total !== null
    ? `${creditsRemaining}/${total} credits`
    : "…";
  const msgsLabel = msgStats ? `${msgStats.used}/${msgStats.limit} msgs` : "…";

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "inline-block" }}
      data-credits-counter="true"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        title="Click for credit return time & message limit details"
        aria-expanded={open}
        style={{
          background: "linear-gradient(180deg, #ffffff12 0%, #ffffff08 100%)",
          border: "1px solid #ffffff25",
          borderRadius: 16,
          color: "#e0c0d8",
          padding: "5px 12px",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          lineHeight: 1.2,
          transition: "border-color 0.2s, background 0.2s",
        }}
      >
        <span>{creditsLabel}</span>
        <span style={{ color: "#ffffff40" }}>·</span>
        <span>{msgsLabel}</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 290,
            maxWidth: "calc(100vw - 32px)",
            background: "#1c1722",
            border: "1px solid #ffffff25",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.6)",
            zIndex: 1000,
            color: "#fff",
            fontSize: 13,
            textAlign: "left",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: "1px solid #ffffff15",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14, color: "#e0c0d8" }}>
              Usage & Quotas
            </span>
            <span style={{ fontSize: 11, color: "#888" }}>
              30-day window
            </span>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              <span>🎬 Search Credits</span>
              <span
                style={{
                  color: creditsRemaining !== null && creditsRemaining <= 2
                    ? "#f87171"
                    : "#4ade80",
                }}
              >
                {creditsRemaining !== null && total !== null
                  ? `${creditsRemaining} / ${total} left`
                  : "…"}
              </span>
            </div>
            {creditsRemaining !== null && total !== null && total > 0 && (
              <div
                style={{
                  height: 6,
                  width: "100%",
                  background: "#ffffff15",
                  borderRadius: 3,
                  overflow: "hidden",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${
                      Math.min(
                        100,
                        Math.max(0, (creditsRemaining / total) * 100),
                      )
                    }%`,
                    background: creditsRemaining <= 2 ? "#f87171" : "#a855f7",
                    transition: "width 0.3s",
                  }}
                />
              </div>
            )}
            <div style={{ fontSize: 12, color: "#ccc", lineHeight: 1.4 }}>
              {nextReturn !== null && nextReturn > 0
                ? (
                  <div style={{ color: "#fcd34d" }}>
                    ⚡ Next credit in{" "}
                    <strong>{formatDuration(nextReturn)}</strong>{" "}
                    ({formatTargetDate(nextReturn)})
                  </div>
                )
                : creditsRemaining === total
                ? (
                  <div style={{ color: "#86efac" }}>
                    ✓ All search credits available
                  </div>
                )
                : (
                  <div style={{ color: "#aaa" }}>
                    Search credits return 30 days after each search.
                  </div>
                )}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              <span>💬 AI Messages</span>
              <span
                style={{
                  color: msgStats && msgStats.remaining <= 5
                    ? "#f87171"
                    : "#60a5fa",
                }}
              >
                {msgStats ? `${msgStats.used} / ${msgStats.limit} used` : "…"}
              </span>
            </div>
            {msgStats && msgStats.limit > 0 && (
              <div
                style={{
                  height: 6,
                  width: "100%",
                  background: "#ffffff15",
                  borderRadius: 3,
                  overflow: "hidden",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${
                      Math.min(
                        100,
                        Math.max(0, (msgStats.used / msgStats.limit) * 100),
                      )
                    }%`,
                    background: msgStats.remaining <= 5 ? "#f87171" : "#3b82f6",
                    transition: "width 0.3s",
                  }}
                />
              </div>
            )}
            <div style={{ fontSize: 12, color: "#ccc", lineHeight: 1.4 }}>
              {msgStats && msgStats.resetMs !== null && msgStats.resetMs > 0
                ? (
                  <div style={{ color: "#93c5fd" }}>
                    🔄 Limit resets in{" "}
                    <strong>{formatDuration(msgStats.resetMs)}</strong>{" "}
                    ({formatTargetDate(msgStats.resetMs)})
                  </div>
                )
                : (
                  <div style={{ color: "#aaa" }}>
                    {msgStats
                      ? `${msgStats.remaining} messages remaining this month.`
                      : "Messages reset monthly."}
                  </div>
                )}
            </div>
          </div>

          <div
            style={{
              paddingTop: 8,
              borderTop: "1px solid #ffffff15",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              textAlign: "center",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openHistoryModal("downloads");
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background:
                  "linear-gradient(180deg, #ffffff15 0%, #ffffff08 100%)",
                border: "1px solid #ffffff25",
                color: "#e0c0d8",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                boxSizing: "border-box",
                textAlign: "center",
              }}
            >
              📜 View Download & Billing History
            </button>
            <a
              href={ROUTES.PRICING}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background: "#311d2b",
                color: "#e0c0d8",
                borderRadius: 6,
                textDecoration: "none",
                fontSize: 12,
                fontWeight: 600,
                boxSizing: "border-box",
              }}
            >
              Get More Credits / Upgrade Pro
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const discordUrl = "https://discord.gg/xdEFs8Wftg";

const NavLinks = () => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      justifyContent: useIsMobile() ? "center" : "right",
    }}
  >
    {!useIsMobile() && <CreditsLeft />}
    <a href={ROUTES.APP} style={navLinkStyle}>Chat</a>
    <a href={ROUTES.PRICING} style={navLinkStyle}>Pricing</a>
    <a href={ROUTES.DOCS} style={navLinkStyle}>Docs</a>
    {userEmail.value && (
      <a href={ROUTES.SETTINGS} style={navLinkStyle}>Settings</a>
    )}
  </div>
);

const DesktopFooter = () =>
  useIsMobile() ? null : (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        marginTop: "auto",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <SocialIcon
          style={{ width: 28, height: 28 }}
          url={discordUrl}
          bgColor="#1a1a1a"
          network="discord"
        />
        <SocialIcon
          style={{ width: 28, height: 28 }}
          url="https://twitter.com/findscene"
          bgColor="#1a1a1a"
          network="twitter"
        />
        <SocialIcon
          style={{ width: 28, height: 28 }}
          url="https://t.me/quote_from_movie_bot"
          bgColor="#1a1a1a"
          network="telegram"
        />
        <SocialIcon
          style={{ width: 28, height: 28 }}
          url="mailto:support@find-scene.com"
          bgColor="#1a1a1a"
          network="email"
        />
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#666" }}>
          Built with{" "}
          <a
            href="https://prompt2bot.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#666" }}
          >
            prompt2bot.com
          </a>
        </span>
        <a href={ROUTES.TERMS} style={{ fontSize: 12, color: "#666" }}>
          Terms
        </a>
        <a href={ROUTES.PRIVACY} style={{ fontSize: 12, color: "#666" }}>
          Privacy
        </a>
      </div>
    </div>
  );

const showFooterMobile = signal(false);
const chatCustomColors = {
  primary: "#311d2b",
  background: "#1a1a1a",
  text: "#ccc",
  hideTitle: true,
  hideOwnAvatar: true,
  hideOtherBubble: true,
  hideNames: true,
  inputBackground: "transparent",
  inputMaxWidth: "700px",
  chatMaxWidth: "900px",
};

const withTimeout = <T,>(
  promise: Promise<T>,
  timeoutErrorMessage: string,
  timeoutMs: number,
): Promise<T> => {
  let timer: any;
  let hasTimedOut = false;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      hasTimedOut = true;
      reject(new Error(timeoutErrorMessage));
    }, timeoutMs);
  });

  const mainPromise = promise.then(
    (res) => {
      clearTimeout(timer);
      return res;
    },
    (err) => {
      clearTimeout(timer);
      throw err;
    },
  );

  mainPromise.catch((err) => {
    if (hasTimedOut) {
      console.warn(
        "Promise rejected after timeout (suppressed unhandled rejection):",
        err,
      );
    }
  });

  return Promise.race([mainPromise, timeoutPromise]);
};

const getOrCreateConversation = async (
  setConversationId: (id: string) => void,
  participants: string[],
  credentials: Credentials,
  sayText: string,
) => {
  if (
    typeof window !== "undefined" &&
    (window as any).__MOCK_getOrCreateConversation__
  ) {
    return (window as any).__MOCK_getOrCreateConversation__(
      setConversationId,
      participants,
      credentials,
      sayText,
    );
  }
  const conversationType = "chat";
  const timeoutMs = 10_000;
  const existing = await withTimeout(
    getConversations(participants),
    "Failed to load existing chat conversation",
    timeoutMs,
  );
  if (Array.isArray(existing) && existing.length > 0) {
    const conversationId = existing[0].id;
    setConversationId(conversationId);
    return conversationId;
  }
  const created = await withTimeout(
    createConversation(
      participants,
      conversationType,
      credentials,
    ),
    "Failed to create chat conversation",
    timeoutMs,
  );
  if ("conversationId" in created) {
    const conversationId = created.conversationId;
    setConversationId(conversationId);
    if (sayText) {
      await sendMessage({
        credentials,
        conversation: conversationId,
        message: { text: sayText, type: "text" },
      });
    }
    return conversationId;
  }
  const convos = await withTimeout(
    getConversations(participants),
    "Failed to fetch chat conversation after creation",
    timeoutMs,
  );
  if (Array.isArray(convos) && convos.length > 0) {
    const conversationId = convos[0].id;
    setConversationId(conversationId);
    if (sayText) {
      await sendMessage({
        credentials,
        conversation: conversationId,
        message: { text: sayText, type: "text" },
      });
    }
    return conversationId;
  }
  throw new Error("Failed to create or retrieve chat conversation");
};

const ChatApp = () => {
  const sayText = typeof window !== "undefined"
    ? new URL(globalThis.window.location.href).searchParams.get("say") ?? ""
    : "";
  const credentials = typeof window !== "undefined" &&
      (window as any).__MOCK_useAliceAndBotCredentials__
    ? (window as any).__MOCK_useAliceAndBotCredentials__(
      userEmail.value ?? undefined,
    )
    : useAliceAndBotCredentials(userEmail.value ?? undefined);
  const key = botPublicSignKey.value;
  const [conversationId, setConversationId] = useState<string>("");
  const [conversationError, setConversationError] = useState<string>("");
  const chatElementReady = chatElementReadySignal.value;
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatHostRef = useRef<HTMLDivElement | null>(null);
  const chatElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const html = document.documentElement;
    const body = document.body;
    const app = document.getElementById("app");
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
      appHeight: app?.style.height ?? "",
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100dvh";
    body.style.height = "100dvh";
    if (app) app.style.height = "100dvh";
    return () => {
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.height = prev.bodyHeight;
      if (app) app.style.height = prev.appHeight;
    };
  }, []);

  useEffect(() => {
    if (chatElementError) {
      setConversationError(chatElementError);
    }
  }, [chatElementReady]);

  useEffect(() => {
    if (!credentials || !key) return;
    setConversationError("");
    getOrCreateConversation(
      setConversationId,
      [credentials.publicSignKey, key],
      credentials,
      sayText,
    ).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Failed to initialize chat conversation", error);
      setConversationError(message);
    });
  }, [credentials?.publicSignKey, key]);

  useEffect(() => {
    if (!chatElementReady || !chatHostRef.current || chatElementRef.current) {
      return;
    }
    const element = document.createElement("alice-connected-chat");
    chatElementRef.current = element;
    chatHostRef.current.appendChild(element);
    return () => {
      element.remove();
      chatElementRef.current = null;
    };
  }, [chatElementReady, !!conversationId, !!credentials]);

  useEffect(() => {
    if (!chatElementRef.current || !conversationId || !credentials) return;
    Object.assign(chatElementRef.current, {
      conversationId,
      credentials,
      darkModeOverride: true,
      customColors: chatCustomColors,
    });
  }, [chatElementReady, conversationId, credentials?.publicSignKey]);

  if (!key || !credentials || !conversationId || !chatElementReady) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          flex: 1,
        }}
      >
        <Spinner />
        {conversationError && (
          <div style={{ fontSize: 12, color: "#999", maxWidth: 560 }}>
            {conversationError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={chatContainerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        ref={chatHostRef}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      />
    </div>
  );
};

const dividerStyle: JSX.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  color: "#666",
  fontSize: 14,
};

const dividerLineStyle: JSX.CSSProperties = {
  flex: 1,
  height: 1,
  background: "#ffffff15",
};

const SignUp = () => {
  const [sentEmail, setSentEmail] = useState("");
  const authUser = instantActiveUser.value;
  const email = userEmail.value;
  const refreshToken = authUser?.refresh_token;
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);

  useEffect(() => {
    if (!email || !refreshToken) return;
    const pendingPriceId = typeof window !== "undefined"
      ? (sessionStorage.getItem("pending_subscribe_price_id") ||
        new URLSearchParams(globalThis.location.search).get("subscribe"))
      : null;
    if (!pendingPriceId) return;
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("pending_subscribe_price_id");
    }
    setRedirectingToCheckout(true);
    import("./db.ts").then(({ createCheckoutSession }) =>
      createCheckoutSession(
        email,
        refreshToken,
        pendingPriceId,
        getTwitterClaimMetadata(),
      )
    ).then((url) => {
      globalThis.window.location.href = url;
    }).catch((err) => {
      console.error("Auto-checkout failed:", err);
      setRedirectingToCheckout(false);
      toast.error("Failed to redirect to checkout. Please visit pricing.");
    });
  }, [email, refreshToken]);

  if (redirectingToCheckout) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          gap: 16,
          minHeight: "50vh",
        }}
      >
        <Spinner />
        <p style={{ color: "#bbb", fontSize: 16 }}>
          Redirecting to Stripe checkout...
        </p>
      </div>
    );
  }

  if (authLoading.value && !instantActiveUser.value) return <Spinner />;
  const hasPendingSubscribe = typeof window !== "undefined" &&
    !!(sessionStorage.getItem("pending_subscribe_price_id") ||
      new URLSearchParams(globalThis.location.search).get("subscribe"));
  return (
    <>
      {instantActiveUser.value ? <ChatApp /> : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 40,
            flexGrow: 1,
            justifyContent: "center",
            padding: "40px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 12,
              maxWidth: 520,
            }}
          >
            <img alt="logo" src="/favicon.svg" style={{ width: 80 }} />
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              {hasPendingSubscribe
                ? "Sign in to complete subscription"
                : "Sign in to find-scene"}
            </h1>
            <p
              style={{ margin: 0, fontSize: 15, color: "#999", maxWidth: 400 }}
            >
              {hasPendingSubscribe
                ? "Complete sign-in to proceed directly to checkout."
                : "Search, extract, and share your favorite TV & movie moments."}
            </p>
          </div>

          <DemoVideo />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
              width: "100%",
              maxWidth: 340,
            }}
          >
            <a
              href={typeof window !== "undefined" ? authUrl() : "#"}
              style={primaryButtonBlockStyle}
            >
              Login with Google
            </a>
            <div style={dividerStyle}>
              <span style={dividerLineStyle} />
              <span>or</span>
              <span style={dividerLineStyle} />
            </div>
            {!sentEmail
              ? <Email setSentEmail={setSentEmail} />
              : <MagicCode sentEmail={sentEmail} />}
          </div>
        </div>
      )}
    </>
  );
};

const userEmail = computed(() => instantActiveUser.value?.email);

const closeMobileMenu = () => {
  showFooterMobile.value = false;
};

const Hamburger = () => {
  const open = showFooterMobile.value;
  const barBase: JSX.CSSProperties = {
    width: "100%",
    height: 2,
    background: "#ccc",
    borderRadius: 1,
    transition: "transform 0.25s ease, opacity 0.2s ease",
    transformOrigin: "center",
  };
  return (
    <button
      type="button"
      onClick={() => {
        showFooterMobile.value = !showFooterMobile.value;
      }}
      data-mobile-menu-button="true"
      aria-expanded={open}
      aria-controls="mobile-nav-menu"
      aria-label={open ? "Close menu" : "Open menu"}
      style={{
        all: "unset",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 5,
        width: 20,
        height: 20,
        padding: 8,
        marginRight: -8,
        cursor: "pointer",
        opacity: 0.8,
        background: "transparent",
      }}
    >
      <span
        style={{
          ...barBase,
          transform: open ? "translateY(7px) rotate(45deg)" : "none",
        }}
      />
      <span style={{ ...barBase, opacity: open ? 0 : 1 }} />
      <span
        style={{
          ...barBase,
          transform: open ? "translateY(-7px) rotate(-45deg)" : "none",
        }}
      />
    </button>
  );
};

const UserEmailWithBadge = () => {
  const email = userEmail.value;
  const isPro = useUserIsPro(email);
  if (!email) return null;
  return (
    <span
      style={{
        fontSize: 14,
        color: "#999",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {email}
      <span
        style={{
          fontSize: 10,
          padding: "2px 6px",
          borderRadius: 4,
          background: isPro ? "#e8c872" : "#333",
          color: isPro ? "#101010" : "#999",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {isPro ? "Pro" : "Free"}
      </span>
    </span>
  );
};

const Headline = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      verticalAlign: "bottom",
    }}
  >
    <a
      href={ROUTES.HOME}
      style={{
        fontSize: 24,
        userSelect: "none",
        lineHeight: 1,
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
      }}
      aria-label="find-scene.com home"
    >
      <img
        src="/favicon.svg"
        alt="find-scene"
        width={32}
        height={32}
        style={{ display: "block" }}
      />
    </a>
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 20,
        alignItems: "center",
      }}
    >
      {useIsMobile() && <Hamburger />}
      {!useIsMobile() && <NavLinks />}
      {!useIsMobile() && userEmail.value && <UserEmailWithBadge />}
      {!useIsMobile() && userEmail.value && (
        <button
          type="button"
          onClick={() => {
            signOut();
          }}
          style={{
            background: "none",
            border: "none",
            color: "#999",
            cursor: "pointer",
            fontSize: 14,
            padding: 0,
          }}
        >
          Sign out
        </button>
      )}
    </div>
  </div>
);

const Header = () => (
  (() => {
    const isMobile = useIsMobile();
    const open = showFooterMobile.value;
    const menuRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      if (!isMobile) {
        closeMobileMenu();
        return;
      }
      const onPointerDown = (event: MouseEvent) => {
        if (!open) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (menuRef.current?.contains(target)) return;
        if (target.closest('[data-mobile-menu-button="true"]')) return;
        closeMobileMenu();
      };
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") closeMobileMenu();
      };
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("mousedown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    }, [isMobile, open]);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          padding: "10px 24px",
        }}
      >
        <Headline />
        {isMobile && (
          <div
            id="mobile-nav-menu"
            ref={menuRef}
            style={{
              display: "flex",
              flexDirection: "column",
              position: "absolute",
              top: 44,
              right: 8,
              width: 240,
              padding: 12,
              gap: 12,
              border: "1px solid #2b2b2b",
              borderRadius: 12,
              background: "#101010",
              boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
              zIndex: 50,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-6px)",
              transition: "opacity 0.18s ease, transform 0.18s ease",
              pointerEvents: open ? "auto" : "none",
              visibility: open ? "visible" : "hidden",
            }}
          >
            <a
              href={ROUTES.APP}
              onClick={closeMobileMenu}
              style={navLinkStyle}
            >
              Chat
            </a>
            <a
              href={ROUTES.PRICING}
              onClick={closeMobileMenu}
              style={navLinkStyle}
            >
              Pricing
            </a>
            <a
              href={ROUTES.DOCS}
              onClick={closeMobileMenu}
              style={navLinkStyle}
            >
              Docs
            </a>
            {userEmail.value && (
              <a
                href={ROUTES.SETTINGS}
                onClick={closeMobileMenu}
                style={navLinkStyle}
              >
                Settings
              </a>
            )}
            <CreditsLeft />
            {userEmail.value && <UserEmailWithBadge />}
            {userEmail.value && (
              <button
                type="button"
                onClick={() => {
                  closeMobileMenu();
                  signOut();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#999",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                  textAlign: "left",
                }}
              >
                Sign out
              </button>
            )}
            <div
              style={{
                borderTop: "1px solid #2a2a2a",
                paddingTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "center",
              }}
            >
              <SocialIcon
                style={{ width: 24, height: 24 }}
                url={discordUrl}
                bgColor="#1a1a1a"
                network="discord"
              />
              <SocialIcon
                style={{ width: 24, height: 24 }}
                url="https://twitter.com/findscene"
                bgColor="#1a1a1a"
                network="twitter"
              />
              <SocialIcon
                style={{ width: 24, height: 24 }}
                url="https://t.me/quote_from_movie_bot"
                bgColor="#1a1a1a"
                network="telegram"
              />
              <SocialIcon
                style={{ width: 24, height: 24 }}
                url="mailto:support@find-scene.com"
                bgColor="#1a1a1a"
                network="email"
              />
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#666" }}>
                  Built with{" "}
                  <a
                    href="https://prompt2bot.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#666" }}
                  >
                    prompt2bot.com
                  </a>
                </span>
                <a
                  href={ROUTES.TERMS}
                  onClick={closeMobileMenu}
                  style={{ fontSize: 12, color: "#666" }}
                >
                  Terms
                </a>
                <a
                  href={ROUTES.PRIVACY}
                  onClick={closeMobileMenu}
                  style={{ fontSize: 12, color: "#666" }}
                >
                  Privacy
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })()
);

const errorMessage = (err: unknown): string =>
  (err as { body?: { message?: string } })?.body?.message ??
    (err instanceof Error ? err.message : String(err));

const submitMagicCode = (email: string, code: string): Promise<unknown> =>
  typeof window !== "undefined" &&
    (window as any).__MOCK_signInWithMagicCode__
    ? (window as any).__MOCK_signInWithMagicCode__({ email, code })
    : signInWithMagicCode({ email, code });

const requestMagicCode = (email: string): Promise<unknown> =>
  typeof window !== "undefined" && (window as any).__MOCK_sendMagicCode__
    ? (window as any).__MOCK_sendMagicCode__(email)
    : sendMagicCode(email);

const Email = ({ setSentEmail }: { setSentEmail: (email: string) => void }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setSubmitting(true);
        requestMagicCode(email)
          .then(() => setSentEmail(email))
          .catch((err) => {
            console.error("send magic code failed", err);
            const message = errorMessage(err) ||
              "Couldn't send the code. Please try again.";
            toast.error(message);
            setSubmitting(false);
          });
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
      }}
    >
      <input
        placeholder="Enter your email"
        type="email"
        value={email}
        onInput={(e) => {
          setEmail(e.currentTarget.value);
        }}
        style={loginInputStyle}
      />
      <button
        type="submit"
        disabled={submitting}
        style={{ ...primaryButtonBlockStyle, opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? "Sending..." : "Get login code"}
      </button>
    </form>
  );
};

const MagicCode = ({ sentEmail }: { sentEmail: string }) => {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (submitting || !code) return;
        setSubmitting(true);
        setError(null);
        submitMagicCode(sentEmail, code).catch((err) => {
          console.error("magic code sign-in failed", err);
          const message = errorMessage(err) ||
            "That code didn't work. Please check it and try again.";
          setError(message);
          toast.error(message);
          setSubmitting(false);
        });
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
      }}
    >
      <p
        style={{ margin: 0, fontSize: 15, color: "#999", textAlign: "center" }}
      >
        We sent you an email! Enter the code below.
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456..."
        value={code}
        onInput={(e) => {
          setCode(e.currentTarget.value);
          setError(null);
        }}
        style={loginInputStyle}
      />
      {error && (
        <p
          data-testid="magic-code-error"
          style={{
            margin: 0,
            fontSize: 13,
            color: "#ff6b6b",
            textAlign: "center",
          }}
        >
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        style={{ ...primaryButtonBlockStyle, opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? "Verifying..." : "Verify"}
      </button>
    </form>
  );
};

// --- API Docs ---

type OpenApiSchema = {
  type?: string;
  properties?: Record<string, OpenApiSchema>;
  required?: string[];
  description?: string;
  items?: OpenApiSchema;
  allOf?: OpenApiSchema[];
  $ref?: string;
  additionalProperties?: boolean;
  enum?: string[];
};

type OpenApiEndpoint = {
  operationId: string;
  summary: string;
  requestBody?: {
    required: boolean;
    content: {
      "application/json": {
        schema: OpenApiSchema;
      };
    };
  };
  responses: Record<
    string,
    { description: string; content?: Record<string, { schema: OpenApiSchema }> }
  >;
};

type OpenApiSpec = {
  info: { title: string; version: string; description: string };
  paths: Record<string, Record<string, OpenApiEndpoint>>;
  components?: { schemas?: Record<string, OpenApiSchema> };
};

const resolveRef = (
  spec: OpenApiSpec,
  schema: OpenApiSchema,
): OpenApiSchema => {
  if (schema.$ref) {
    const refPath = schema.$ref.replace("#/", "").split("/");
    let resolved: any = spec;
    for (const part of refPath) resolved = resolved[part];
    return resolved as OpenApiSchema;
  }
  return schema;
};

const flattenAllOf = (
  spec: OpenApiSpec,
  schema: OpenApiSchema,
): OpenApiSchema => {
  if (!schema.allOf) return resolveRef(spec, schema);
  const merged: OpenApiSchema = {
    type: "object",
    properties: {},
    required: [],
  };
  for (const part of schema.allOf) {
    const resolved = resolveRef(spec, part);
    if (resolved.properties) {
      merged.properties = { ...merged.properties, ...resolved.properties };
    }
    if (resolved.required) {
      merged.required = [...(merged.required ?? []), ...resolved.required];
    }
  }
  return merged;
};

const codeBg = "#2a2a2a";
const codeBorder = "#444";

const ParamRow = ({
  name,
  schema,
  required,
}: {
  name: string;
  schema: OpenApiSchema;
  required: boolean;
}) => (
  <tr>
    <td
      style={{
        padding: "6px 12px",
        fontFamily: "monospace",
        color: "#e8c872",
        wordBreak: "break-all",
      }}
    >
      {name}
      {required && <span style={{ color: "#e06c75" }}>*</span>}
    </td>
    <td style={{ padding: "6px 12px", color: "#888", wordBreak: "break-word" }}>
      {schema.type ?? "object"}
      {schema.enum && ` (${schema.enum.join(" | ")})`}
    </td>
    <td style={{ padding: "6px 12px", color: "#aaa", wordBreak: "break-word" }}>
      {schema.description ?? ""}
    </td>
  </tr>
);

const EndpointCard = ({
  method,
  path,
  endpoint,
  spec,
}: {
  method: string;
  path: string;
  endpoint: OpenApiEndpoint;
  spec: OpenApiSpec;
}) => {
  const [expanded, setExpanded] = useState(false);
  const methodColor = method === "GET" ? "#61afef" : "#98c379";
  const bodySchema = endpoint.requestBody
    ? flattenAllOf(
      spec,
      endpoint.requestBody.content["application/json"].schema,
    )
    : null;
  const params = bodySchema?.properties
    ? Object.entries(bodySchema.properties).filter(([k]) => k !== "_token")
    : [];
  const requiredFields = new Set(
    (bodySchema?.required ?? []).filter((r) => r !== "_token"),
  );

  return (
    <div
      style={{
        border: `1px solid ${codeBorder}`,
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          cursor: "pointer",
          background: codeBg,
        }}
      >
        <span
          style={{
            fontFamily: "monospace",
            fontWeight: "bold",
            color: methodColor,
            minWidth: 50,
          }}
        >
          {method}
        </span>
        <span
          style={{
            fontFamily: "monospace",
            color: "#ddd",
            wordBreak: "break-all",
            minWidth: 0,
          }}
        >
          {path}
        </span>
        <span style={{ color: "#888", marginLeft: "auto", fontSize: 14 }}>
          {expanded ? "\u25B2" : "\u25BC"}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: "12px 16px" }}>
          <p style={{ color: "#ccc", margin: "0 0 12px" }}>
            {endpoint.summary}
          </p>
          {params.length > 0 && (
            <>
              <h4 style={{ margin: "8px 0 4px", color: "#ddd" }}>Parameters</h4>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${codeBorder}` }}>
                      <th
                        style={{
                          padding: "6px 12px",
                          textAlign: "left",
                          color: "#888",
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: "6px 12px",
                          textAlign: "left",
                          color: "#888",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "6px 12px",
                          textAlign: "left",
                          color: "#888",
                        }}
                      >
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {params.map(([name, schema]) => (
                      <ParamRow
                        key={name}
                        name={name}
                        schema={schema}
                        required={requiredFields.has(name)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {method === "POST" && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ margin: "8px 0 4px", color: "#ddd" }}>
                Example request
              </h4>
              <pre
                style={{
                  background: codeBg,
                  padding: 12,
                  borderRadius: 6,
                  fontSize: 13,
                  overflowX: "auto",
                  color: "#abb2bf",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {`curl -X POST https://api.find-scene.com${path} \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(
                  {
                    _token: "YOUR_API_TOKEN",
                    ...Object.fromEntries(
                      params.map(([name, s]) => [
                        name,
                        requiredFields.has(name)
                          ? s.type === "number" ? 0 : `<${name}>`
                          : undefined,
                      ]).filter(([, v]) => v !== undefined),
                    ),
                  },
                  null,
                  2,
                )}'`}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Docs = () => {
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetch("https://api.find-scene.com/api/openapi.json")
      .then((r) => r.json())
      .then(setSpec)
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return <div style={{ padding: 20 }}>Failed to load API docs: {error}</div>;
  }
  if (!spec) {
    return <Spinner />;
  }

  const endpoints = Object.entries(spec.paths).flatMap(([path, methods]) =>
    Object.entries(methods).map(([method, endpoint]) => ({
      method: method.toUpperCase(),
      path,
      endpoint,
    }))
  );

  return (
    <div
      style={{
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          padding: 20,
          maxWidth: 900,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <h1>{spec.info.title}</h1>
        <p style={{ color: "#aaa", marginBottom: 8 }}>
          {spec.info.description}
        </p>
        <p style={{ color: "#888", fontSize: 14 }}>
          Version {spec.info.version} | Base URL:{" "}
          <code style={{ color: "#e8c872" }}>https://api.find-scene.com</code>
        </p>

        <div
          style={{
            background: codeBg,
            border: `1px solid ${codeBorder}`,
            borderRadius: 8,
            padding: 16,
            margin: "20px 0",
          }}
        >
          <h3 style={{ margin: "0 0 8px" }}>Authentication</h3>
          <p style={{ color: "#aaa", margin: "0 0 8px" }}>
            All POST endpoints require a{" "}
            <code style={{ color: "#e8c872" }}>_token</code>{" "}
            field in the JSON body. Generate your API token from your{" "}
            <a href={ROUTES.SETTINGS}>settings page</a>.
          </p>
          <pre
            style={{
              background: "#222",
              padding: 10,
              borderRadius: 6,
              fontSize: 13,
              color: "#abb2bf",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
          {`{ "_token": "your_api_token_here", ... }`}
          </pre>
        </div>

        <div
          style={{
            background: codeBg,
            border: `1px solid ${codeBorder}`,
            borderRadius: 8,
            padding: 16,
            margin: "20px 0",
          }}
        >
          <h3 style={{ margin: "0 0 8px" }}>Async operations</h3>
          <p style={{ color: "#aaa", margin: 0 }}>
            Endpoints like{" "}
            <code style={{ color: "#e8c872" }}>download_by_time</code> and{" "}
            <code style={{ color: "#e8c872" }}>extract_frame</code> return an
            {" "}
            <code style={{ color: "#e8c872" }}>operationId</code>. Poll{" "}
            <code style={{ color: "#e8c872" }}>
              GET /api/operation/{"{id}"}
            </code>{" "}
            until the status is <code style={{ color: "#98c379" }}>"done"</code>
            {" "}
            or <code style={{ color: "#e06c75" }}>"error"</code>.
          </p>
        </div>

        <div
          style={{
            background: codeBg,
            border: `1px solid ${codeBorder}`,
            borderRadius: 8,
            padding: 16,
            margin: "20px 0",
          }}
        >
          <h3 style={{ margin: "0 0 8px" }}>Use as an AI agent skill</h3>
          <p style={{ color: "#aaa", margin: "0 0 12px" }}>
            Install find-scene as a skill for Claude Code, Cursor, Windsurf, or
            any AI coding agent:
          </p>
          <pre
            style={{
              background: "#222",
              padding: 10,
              borderRadius: 6,
              fontSize: 13,
              color: "#abb2bf",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            npx skills add @uriva/find-scene
          </pre>
          <p style={{ color: "#888", margin: "10px 0 0", fontSize: 13 }}>
            This gives your AI agent the ability to search for movie/TV scenes,
            download clips, and extract frames via the find-scene API.
          </p>
        </div>

        <h2 style={{ marginTop: 30 }}>Endpoints</h2>
        {endpoints.map(({ method, path, endpoint }) => (
          <EndpointCard
            key={`${method}-${path}`}
            method={method}
            path={path}
            endpoint={endpoint}
            spec={spec}
          />
        ))}

        <div
          style={{
            marginTop: 40,
            padding: "20px 0",
            borderTop: `1px solid ${codeBorder}`,
            color: "#666",
            fontSize: 13,
          }}
        >
          <a href="https://api.find-scene.com/api/openapi.json">
            OpenAPI spec (JSON)
          </a>
        </div>
      </div>
    </div>
  );
};

const instantActiveUser = signal<User | null>(null);
const authLoading = signal(true);
const isClient = signal(false);

const settingsCheckboxStyle: JSX.CSSProperties = {
  width: 18,
  height: 18,
  accentColor: "rgb(208, 173, 240)",
  cursor: "pointer",
};

const settingsLabelStyle: JSX.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  cursor: "pointer",
  fontSize: 15,
};

const settingsTableStyle: JSX.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
};

const settingsThStyle: JSX.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid #333",
  color: "#999",
  fontWeight: 500,
};

const settingsTdStyle: JSX.CSSProperties = {
  padding: "10px 12px",
  borderBottom: "1px solid #333",
};

const SettingsCheckbox = (
  { checked, onChange, label }: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
  },
) => (
  <label style={settingsLabelStyle}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
      style={settingsCheckboxStyle}
    />
    {label}
  </label>
);

const TokenTable = (
  { tokens, onRevoke }: {
    tokens: {
      id: string;
      seed: string;
      createdAt: number;
      active: boolean;
      name?: string;
    }[];
    onRevoke: (id: string) => void;
  },
) =>
  tokens.length === 0
    ? (
      <p style={{ color: "#999" }}>
        No API tokens yet. Generate one using the button above.
      </p>
    )
    : (
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={settingsTableStyle}>
          <thead>
            <tr>
              <th style={settingsThStyle}>Name</th>
              <th style={settingsThStyle}>Prefix</th>
              <th style={settingsThStyle}>Created</th>
              <th style={settingsThStyle}>Status</th>
              <th style={settingsThStyle}></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(({ id, seed, createdAt, active, name }) => (
              <tr key={seed}>
                <td style={settingsTdStyle}>
                  {name ?? <span style={{ color: "#666" }}>-</span>}
                </td>
                <td style={{ ...settingsTdStyle, fontFamily: "monospace" }}>
                  {seed.slice(0, 8)}...
                </td>
                <td style={settingsTdStyle}>
                  {new Date(createdAt).toLocaleDateString()}
                </td>
                <td
                  style={{
                    ...settingsTdStyle,
                    color: active ? "#4ade80" : "#999",
                  }}
                >
                  {active ? "Active" : "Revoked"}
                </td>
                <td style={settingsTdStyle}>
                  {active && (
                    <button
                      type="button"
                      onClick={() => onRevoke(id)}
                      style={{
                        ...secondaryButtonStyle,
                        padding: "4px 12px",
                        fontSize: 13,
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

const newTokenBoxStyle: JSX.CSSProperties = {
  background: "#2a2a2a",
  border: "1px solid #311d2b",
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const Settings = () => {
  const email = userEmail.value;
  const { user: authUser } = useAuth();
  const [newToken, setNewToken] = useState<
    { value: string; name: string } | null
  >(null);
  const [tokenName, setTokenName] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [disconnectingTwitter, setDisconnectingTwitter] = useState(false);
  const [twitterHandleInput, setTwitterHandleInput] = useState("");
  const [savingHandle, setSavingHandle] = useState(false);
  if (!email) {
    return (
      <div style={legalPageStyle}>
        <h1 style={legalH1Style}>Settings</h1>
        <p style={{ color: "#999", marginBottom: 24 }}>
          Please sign in to access settings.
        </p>
        <a
          href={ROUTES.APP}
          style={{
            ...primaryButtonStyle,
            display: "inline-block",
            textDecoration: "none",
          }}
        >
          Sign in
        </a>
      </div>
    );
  }
  const user = typeof window !== "undefined" &&
      (window as any).__MOCK_useUserSettings__
    ? (window as any).__MOCK_useUserSettings__(email)
    : useUserSettings(email);
  if (!user) return <div style={legalPageStyle}>Loading...</div>;
  const update = updateUserPref(user.id);
  const tokens = (user as any).apiTokens ?? [];
  const trimmedName = tokenName.trim();
  const refreshToken = authUser?.refresh_token;
  useEffect(() => {
    if (typeof globalThis.location === "undefined" || !email || !refreshToken) {
      return;
    }
    const url = new URL(globalThis.location.href);
    const sessionId = url.searchParams.get("session_id");
    if (!sessionId) return;
    url.searchParams.delete("session_id");
    url.searchParams.delete("checkout");
    globalThis.history.replaceState(
      {},
      "",
      url.pathname + (url.search ? url.search : ""),
    );
    syncCheckoutSession(email, refreshToken, sessionId)
      .then((ok) => {
        if (ok) {
          toast.success("Subscription confirmed! Your credits are ready.");
        }
      })
      .catch((e) => console.error("Failed to sync checkout session:", e));
  }, [email, refreshToken]);

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const url = new URL(globalThis.location.href);
    const linkedHandle = url.searchParams.get("twitter_linked");
    if (linkedHandle) {
      toast.success(`Connected @${linkedHandle} to your account!`);
      url.searchParams.delete("twitter_linked");
      globalThis.history.replaceState(
        {},
        "",
        url.pathname + (url.search ? url.search : ""),
      );
    }
  }, []);

  const onDisconnectTwitter = () => {
    if (!refreshToken) return;
    setDisconnectingTwitter(true);
    disconnectTwitterAccount(email, refreshToken)
      .then((ok) => {
        if (ok) {
          toast.success("Disconnected X account");
        } else {
          toast.error("Failed to disconnect X account");
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to disconnect X account");
      })
      .finally(() => setDisconnectingTwitter(false));
  };

  const onSaveTwitterHandle = () => {
    const trimmed = twitterHandleInput.trim().replace(/^@/, "");
    if (!refreshToken || !trimmed) return;
    setSavingHandle(true);
    linkTwitterHandle(email, refreshToken, trimmed)
      .then((res) => {
        toast.success(`Linked @${res.handle} to your account!`);
        setTwitterHandleInput("");
      })
      .catch((e) => {
        console.error(e);
        toast.error((e as Error)?.message || "Failed to link Twitter handle");
      })
      .finally(() => setSavingHandle(false));
  };
  const canGenerate = !generating && !!refreshToken && trimmedName.length > 0;
  const onGenerate = () => {
    if (!refreshToken || !trimmedName) return;
    setGenerating(true);
    generateApiToken(email, refreshToken, trimmedName)
      .then((value) => {
        setNewToken({ value, name: trimmedName });
        setTokenName("");
        setCopied(false);
      })
      .catch((e) => {
        console.error(e);
        alert("Failed to generate token. Please try again.");
      })
      .finally(() => setGenerating(false));
  };
  return (
    <div style={legalPageStyle}>
      <h1 style={legalH1Style}>Settings</h1>
      <p style={{ color: "#999", marginBottom: 32, overflowWrap: "anywhere" }}>
        {email}
      </p>

      <h2 style={legalH2Style}>Email preferences</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <SettingsCheckbox
          checked={user.unsubscribed !== true}
          onChange={(v) => update("unsubscribed", !v)}
          label="Receive newsletter emails"
        />
        <SettingsCheckbox
          checked={user.emailResults !== false}
          onChange={(v) => update("emailResults", v)}
          label="Receive email for every video result"
        />
      </div>

      <h2 style={{ ...legalH2Style, marginTop: 48 }}>Connected accounts</h2>
      <div style={{ maxWidth: 440 }}>
        {(user as any).twitterHandle
          ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
                borderRadius: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>@{(user as any).twitterHandle}</span>
                </div>
                <div style={{ fontSize: 12, color: "#10b981", marginTop: 2 }}>
                  ✓ Connected (Pro quota shared with @findscene mentions)
                </div>
              </div>
              <button
                type="button"
                onClick={onDisconnectTwitter}
                disabled={disconnectingTwitter}
                style={{
                  background: "transparent",
                  border: "1px solid #3f3f46",
                  color: "#a1a1aa",
                  borderRadius: 6,
                  padding: "6px 12px",
                  cursor: disconnectingTwitter ? "default" : "pointer",
                  fontSize: 13,
                  opacity: disconnectingTwitter ? 0.6 : 1,
                }}
              >
                {disconnectingTwitter ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          )
          : (
            <div>
              <p style={{ color: "#a1a1aa", fontSize: 14, marginBottom: 12 }}>
                Link your X (Twitter) handle so your Pro subscription and
                watermark-free clips apply when you mention @findscene.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="text"
                  placeholder="Twitter handle (e.g. @username)"
                  value={twitterHandleInput}
                  onInput={(e) => setTwitterHandleInput(e.currentTarget.value)}
                  style={{
                    ...loginInputStyle,
                    maxWidth: 240,
                    height: 40,
                  }}
                />
                <button
                  type="button"
                  onClick={onSaveTwitterHandle}
                  disabled={savingHandle || !refreshToken ||
                    !twitterHandleInput.trim()}
                  style={{
                    backgroundColor: "#fff",
                    color: "#000",
                    border: "none",
                    borderRadius: 6,
                    padding: "0 16px",
                    fontWeight: 600,
                    fontSize: 13,
                    height: 40,
                    cursor: savingHandle ? "default" : "pointer",
                    opacity: (savingHandle || !twitterHandleInput.trim())
                      ? 0.6
                      : 1,
                  }}
                >
                  {savingHandle ? "Linking..." : "Link Handle"}
                </button>
              </div>
            </div>
          )}
      </div>

      <h2 style={{ ...legalH2Style, marginTop: 48 }}>API tokens</h2>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Token name (required, e.g. Laptop, Zapier)"
          value={tokenName}
          onInput={(e) => setTokenName(e.currentTarget.value)}
          style={{ ...loginInputStyle, flex: 1, minWidth: 200 }}
        />
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          style={{
            ...primaryButtonStyle,
            opacity: canGenerate ? 1 : 0.6,
            cursor: canGenerate ? "pointer" : "not-allowed",
          }}
        >
          {generating ? "Generating..." : "Generate new token"}
        </button>
      </div>
      {newToken && (
        <div style={newTokenBoxStyle}>
          <p style={{ color: "#e8c872", marginTop: 0, fontWeight: 600 }}>
            Copy "{newToken.name}" token now, it will not be shown again.
          </p>
          <code
            style={{
              display: "block",
              background: "#1a1a1a",
              padding: 12,
              borderRadius: 8,
              wordBreak: "break-all",
              color: "#ccc",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {newToken.value}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(newToken.value);
              setCopied(true);
              toast.success("Token copied to clipboard!");
              setTimeout(() => setCopied(false), 2000);
            }}
            style={{
              ...secondaryButtonStyle,
              marginTop: 12,
              borderColor: copied ? "#e8c872" : "#311d2b",
              color: copied ? "#e8c872" : "#e0c0d8",
            }}
          >
            {copied ? "✓ Copied!" : "Copy to clipboard"}
          </button>
        </div>
      )}
      <TokenTable tokens={tokens} onRevoke={revokeToken} />

      <h2 style={{ ...legalH2Style, marginTop: 48 }}>
        Download & billing history
      </h2>
      <p style={{ color: "#999", marginBottom: 16 }}>
        Track your search credit usage, rolling 30-day download history, and
        payment transactions.
      </p>
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <button
          type="button"
          onClick={() => openHistoryModal("downloads")}
          style={primaryButtonStyle}
        >
          🎬 View Download History
        </button>
        <button
          type="button"
          onClick={() => openHistoryModal("billing")}
          style={secondaryButtonStyle}
        >
          💳 View Billing History
        </button>
      </div>

      <DangerZone email={email} refreshToken={refreshToken} />
    </div>
  );
};

const dangerZoneStyle: JSX.CSSProperties = {
  marginTop: 64,
  padding: 24,
  border: "1px solid #5a2222",
  borderRadius: 8,
  background: "#1a0e0e",
};

const dangerButtonStyle: JSX.CSSProperties = {
  ...primaryButtonStyle,
  background: "#a83232",
  borderColor: "#a83232",
};

const Pricing = () => {
  const { route: navigate } = useLocation();
  const email = userEmail.value;
  const isPro = useUserIsPro(email);
  const currentTierPrice = useUserCurrentTierPrice(email);
  const authUser = instantActiveUser.value;
  const refreshToken = authUser?.refresh_token;
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleSubscribe = async (priceId: string) => {
    if (!email || !refreshToken) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("pending_subscribe_price_id", priceId);
      }
      navigate(`${ROUTES.APP}?subscribe=${priceId}`);
      return;
    }
    setLoadingPriceId(priceId);
    try {
      const { createCheckoutSession } = await import("./db.ts");
      const url = await createCheckoutSession(
        email,
        refreshToken,
        priceId,
        getTwitterClaimMetadata(),
      );
      globalThis.window.location.href = url;
    } catch (err) {
      console.error(err);
      alert("Failed to initiate subscription. Please try again.");
    } finally {
      setLoadingPriceId(null);
    }
  };

  const handleManage = async () => {
    if (!email || !refreshToken) return;
    setPortalLoading(true);
    try {
      const { createPortalSession } = await import("./db.ts");
      const url = await createPortalSession(email, refreshToken);
      globalThis.window.location.href = url;
    } catch (err) {
      console.error(err);
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const pricingCardStyle: JSX.CSSProperties = {
    padding: "32px 24px",
    borderRadius: 16,
    border: "1px solid #ffffff15",
    background: "linear-gradient(180deg, #ffffff0c 0%, #ffffff04 100%)",
    textAlign: "center",
    flex: "1 1 200px",
    maxWidth: 220,
    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    boxSizing: "border-box" as const,
  };

  const pricingHeaderStyle: JSX.CSSProperties = {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: "#e0c0d8",
  };

  const priceStyle: JSX.CSSProperties = {
    fontSize: 40,
    fontWeight: 800,
    margin: 0,
    color: "#fff",
  };

  return (
    <div style={{ ...legalPageStyle, maxWidth: 1000, textAlign: "center" }}>
      <h1 style={{ ...legalH1Style, fontSize: 36, marginBottom: 12 }}>
        Choose Your Plan
      </h1>
      <p
        style={{
          color: "#aaa",
          fontSize: 16,
          marginBottom: 32,
          maxWidth: 600,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        Get instant access to movie and TV show search credits. Rolling 30-day
        window limits.
      </p>

      {email && isPro && (
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            border: "1px solid #e8c87230",
            borderRadius: 8,
            background: "#e8c87208",
            display: "inline-block",
          }}
        >
          <p style={{ color: "#e8c872", fontWeight: 600, margin: "0 0 8px 0" }}>
            You are a Pro Member! ✨
          </p>
          <button
            type="button"
            onClick={handleManage}
            disabled={portalLoading}
            style={secondaryButtonStyle}
          >
            {portalLoading
              ? "Loading Portal..."
              : "Manage Subscription & Billing"}
          </button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          justifyContent: "center",
          marginTop: 12,
        }}
      >
        {/* Free Tier */}
        <div style={pricingCardStyle}>
          <h3 style={pricingHeaderStyle}>Free Tier</h3>
          <p style={priceStyle}>
            $0<span style={{ fontSize: 16, fontWeight: 400, color: "#888" }}>
              / month
            </span>
          </p>
          <p style={{ color: "#bbb", fontSize: 14, margin: "12px 0 24px 0" }}>
            {freeTierQuota} search credits / month
            <br />
            {freeTierMessageLimit} messages / month
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(ROUTES.APP)}
            style={{
              ...primaryButtonStyle,
              marginTop: "auto",
              background: "#ffffff15",
              color: "#fff",
              border: "1px solid #ffffff20",
            }}
          >
            {email && !isPro ? "Active Plan" : "Get Started"}
          </button>
        </div>

        {/* Paid Tiers */}
        {pricingTiers.map(
          ({
            id,
            name,
            priceUSD,
            credits,
            messageLimit,
            stripePriceId,
            popular,
          }) => (
            <div
              key={id}
              style={{
                ...pricingCardStyle,
                ...(popular
                  ? { border: "1px solid #e8c87250", position: "relative" }
                  : {}),
              }}
            >
              {popular && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#e8c872",
                    color: "#101010",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 10px",
                    borderRadius: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Most Popular
                </div>
              )}
              <h3 style={pricingHeaderStyle}>{name}</h3>
              <p style={priceStyle}>
                ${priceUSD}
                <span
                  style={{ fontSize: 16, fontWeight: 400, color: "#888" }}
                >
                  / month
                </span>
              </p>
              <p
                style={{ color: "#bbb", fontSize: 14, margin: "12px 0 24px 0" }}
              >
                {credits} search credits / month
                <br />
                {messageLimit} messages / month
              </p>
              {email && isPro && currentTierPrice === priceUSD
                ? (
                  <button
                    type="button"
                    onClick={handleManage}
                    disabled={portalLoading}
                    style={{
                      ...primaryButtonStyle,
                      marginTop: "auto",
                      background: "#ffffff15",
                      color: "#fff",
                      border: "1px solid #ffffff20",
                    }}
                  >
                    Active Plan
                  </button>
                )
                : email && isPro
                ? (
                  <button
                    type="button"
                    onClick={handleManage}
                    disabled={portalLoading}
                    style={{
                      ...primaryButtonStyle,
                      ...(popular && priceUSD > currentTierPrice
                        ? { background: "#e8c872", color: "#101010" }
                        : {}),
                      marginTop: "auto",
                    }}
                  >
                    {portalLoading
                      ? "Loading Portal..."
                      : priceUSD > currentTierPrice
                      ? "Upgrade in Portal"
                      : "Switch in Portal"}
                  </button>
                )
                : (
                  <button
                    type="button"
                    onClick={() => handleSubscribe(stripePriceId)}
                    disabled={!!loadingPriceId}
                    style={{
                      ...primaryButtonStyle,
                      ...(popular
                        ? { background: "#e8c872", color: "#101010" }
                        : {}),
                      marginTop: "auto",
                    }}
                  >
                    {loadingPriceId === stripePriceId
                      ? "Processing..."
                      : "Subscribe Now"}
                  </button>
                )}
            </div>
          ),
        )}
      </div>
    </div>
  );
};

const DangerZone = (
  { email, refreshToken }: { email: string; refreshToken?: string },
) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canDelete = !deleting && !!refreshToken && confirmText === "DELETE";
  const onDelete = () =>
    refreshToken && confirmText === "DELETE" &&
    (setDeleting(true),
      deleteAccount(email, refreshToken)
        .then(() => signOut())
        .then(() => {
          globalThis.location.href = "/";
        })
        .catch((e) => {
          console.error(e);
          alert("Failed to delete account. Please try again.");
          setDeleting(false);
        }));
  return (
    <div style={dangerZoneStyle}>
      <h2 style={{ ...legalH2Style, marginTop: 0, color: "#e87272" }}>
        Danger zone
      </h2>
      <p style={{ color: "#bbb" }}>
        Delete your account and all associated data: video results, messages,
        API tokens, and payment history. Any remaining paid credits will be
        forfeited. Free-tier usage from the last 30 days will continue to count
        against the same email if you sign up again.
      </p>
      {confirmOpen
        ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <p style={{ color: "#e87272", fontWeight: 600, margin: 0 }}>
              Type DELETE to confirm. This cannot be undone.
            </p>
            <input
              type="text"
              value={confirmText}
              onInput={(e) => setConfirmText(e.currentTarget.value)}
              style={{ ...loginInputStyle, maxWidth: 240 }}
            />
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={onDelete}
                disabled={!canDelete}
                style={{
                  ...dangerButtonStyle,
                  opacity: canDelete ? 1 : 0.6,
                  cursor: canDelete ? "pointer" : "not-allowed",
                }}
              >
                {deleting ? "Deleting..." : "Permanently delete account"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmText("");
                }}
                disabled={deleting}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        )
        : (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            style={dangerButtonStyle}
          >
            Delete account
          </button>
        )}
    </div>
  );
};

const RedirectToApp = () => {
  const { route: navigate } = useLocation();
  useEffect(() => {
    navigate(ROUTES.APP, true);
  }, []);
  return null;
};

const getTwitterClaimMetadata = (): Record<string, string> | undefined => {
  try {
    const raw = typeof globalThis !== "undefined"
      ? globalThis.sessionStorage?.getItem("pending_twitter_claim")
      : null;
    if (!raw) return undefined;
    const { id, handle } = JSON.parse(raw);
    if (id && handle) {
      return { twitterUserId: id, twitterHandle: handle };
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const pendingTwitterClaim = signal<
  {
    id: string;
    handle: string;
    sig: string;
  } | null
>(null);

const TwitterClaimBanner = () => {
  const claim = pendingTwitterClaim.value;
  const { user } = useAuth();
  const [claiming, setClaiming] = useState(false);
  if (!claim || !user?.email || !user?.refresh_token) return null;

  const onConfirm = () => {
    setClaiming(true);
    claimTwitterHandle(user.email!, user.refresh_token!, claim)
      .then((res) => {
        if (res.ok) {
          toast.success(`Connected @${res.handle} to your account!`);
          pendingTwitterClaim.value = null;
          try {
            globalThis.sessionStorage?.removeItem("pending_twitter_claim");
          } catch {
            // ignore
          }
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error((e as Error)?.message || "Failed to link Twitter account");
      })
      .finally(() => setClaiming(false));
  };

  const onDismiss = () => {
    pendingTwitterClaim.value = null;
    try {
      globalThis.sessionStorage?.removeItem("pending_twitter_claim");
    } catch {
      // ignore
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#18181b",
        borderBottom: "1px solid #27272a",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
        zIndex: 50,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        <span style={{ fontSize: 14, color: "#e4e4e7" }}>
          Link Twitter account{" "}
          <strong style={{ color: "#fff" }}>@{claim.handle}</strong> to{" "}
          <strong style={{ color: "#fff" }}>{user.email}</strong>?
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={onDismiss}
          disabled={claiming}
          style={{
            background: "transparent",
            border: "1px solid #3f3f46",
            color: "#a1a1aa",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            cursor: claiming ? "default" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={claiming}
          style={{
            backgroundColor: "#fff",
            color: "#000",
            border: "none",
            borderRadius: 6,
            padding: "6px 14px",
            fontWeight: 600,
            fontSize: 13,
            cursor: claiming ? "default" : "pointer",
            opacity: claiming ? 0.7 : 1,
          }}
        >
          {claiming ? "Connecting..." : `Connect @${claim.handle}`}
        </button>
      </div>
    </div>
  );
};

const AuthSync = () => {
  const { user, isLoading } = useAuth();
  const userSettings = useUserSettings(user?.email || "");

  useEffect(() => {
    isClient.value = true;
    if (
      typeof window !== "undefined" && (window as any).__MOCK_AUTH_OVERRIDE__
    ) {
      return;
    }
    instantActiveUser.value = user || null;
    authLoading.value = isLoading;
  }, [user, isLoading]);

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const url = new URL(globalThis.location.href);
    const twId = url.searchParams.get("tw_id");
    const twH = url.searchParams.get("tw_h");
    const twS = url.searchParams.get("tw_s");
    if (twId && twH && twS) {
      const claim = { id: twId, handle: twH, sig: twS };
      try {
        globalThis.sessionStorage?.setItem(
          "pending_twitter_claim",
          JSON.stringify(claim),
        );
      } catch {
        // sessionStorage might be disabled or restricted
      }
      url.searchParams.delete("tw_id");
      url.searchParams.delete("tw_h");
      url.searchParams.delete("tw_s");
      globalThis.history.replaceState(
        {},
        "",
        url.pathname + (url.search ? url.search : ""),
      );
      if (user?.email && user?.refresh_token) {
        pendingTwitterClaim.value = claim;
      }
    }
  }, [user?.email, user?.refresh_token]);

  useEffect(() => {
    const email = user?.email;
    const refreshToken = user?.refresh_token;
    if (!email || !refreshToken) return;
    try {
      const raw = typeof globalThis !== "undefined"
        ? globalThis.sessionStorage?.getItem("pending_twitter_claim")
        : null;
      if (!raw) return;
      const claim = JSON.parse(raw);
      const currentHandle = (userSettings as any)?.twitterHandle;
      if (
        currentHandle &&
        currentHandle.toLowerCase() === claim.handle.toLowerCase()
      ) {
        globalThis.sessionStorage?.removeItem("pending_twitter_claim");
        return;
      }
      pendingTwitterClaim.value = claim;
    } catch {
      // ignore parsing or storage errors
    }
  }, [user?.email, user?.refresh_token, (userSettings as any)?.twitterHandle]);

  return null;
};

const appShellStyle: JSX.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: "100dvh",
};

const appContentStyle: JSX.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
  minWidth: 0,
};

const App = () => (
  <div style={appShellStyle}>
    {isClient.value && <Toaster />}
    <AuthSync />
    <TwitterClaimBanner />
    <Header />
    <div style={appContentStyle}>
      <Router>
        <Route path={ROUTES.HOME} component={Landing} />
        <Route path={ROUTES.APP} component={SignUp} />
        <Route path={ROUTES.TERMS} component={Terms} />
        <Route path={ROUTES.PRIVACY} component={Privacy} />
        <Route path={ROUTES.SUBS} component={Subs} />
        <Route path={ROUTES.DOCS} component={Docs} />
        <Route path={ROUTES.SETTINGS} component={Settings} />
        <Route path={ROUTES.PRICING} component={Pricing} />
        <Route default component={RedirectToApp} />
      </Router>
    </div>
    {historyModalOpen.value && <HistoryModal />}
    <DesktopFooter />
  </div>
);

export const App_ = () => (
  <LocationProvider>
    <App />
  </LocationProvider>
);

if (typeof window !== "undefined") {
  hydrate(<App_ />);
  (globalThis as any).__TEST_HOOKS__ = {
    botPublicSignKey,
    instantActiveUser,
    authLoading,
    isClient,
    historyModalOpen,
    historyModalTab,
  };
}
